import {
  ClockIcon,
  EyeOpenIcon,
  GridIcon,
  InfoCircledIcon,
  ListBulletIcon,
  MixerHorizontalIcon,
  PieChartIcon,
  RulerSquareIcon,
} from '@radix-ui/react-icons';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'components/ui/accordion';
import Well from 'components/Layout/Well';
import { useChartConfig } from 'hooks/useChartConfig';
import {
  AreaChartIcon,
  BubbleChartIcon,
  LineChartIcon,
} from 'components/Icons/Charts';
import { AnimationForm } from './AnimationForm';
import { AppearanceForm } from './AppearanceForm';
import { AreaForm } from './AreaForm';
import { AxesForm } from './AxesForm';
import { BubbleForm } from './BubbleForm';
import { GridForm } from './GridForm';
import { LineForm } from './LineForm';
import { PieForm } from './PieForm';
import { ReactNode } from 'react';
import { IndicatorsForm } from './IndicatorsForm';

// to-do: add new chart config object for every chart (not all charts share the same config)
const CHART_CONFIG_FORMS: Record<
  string,
  {
    icon: React.ReactNode;
    label: string;
    form: ReactNode;
    defaultOpen: boolean;
  }
> = {
  Indicators: {
    icon: <ListBulletIcon className="h-5 w-5 text-blue-500 inline" />,
    label: 'Indicateurs',
    form: <IndicatorsForm />,
    defaultOpen: true,
  },
  Appearance: {
    icon: <EyeOpenIcon className="h-5 w-5 text-blue-500 inline" />,
    label: 'Apparence',
    form: <AppearanceForm />,
    defaultOpen: true,
  },
  Axis: {
    icon: <RulerSquareIcon className="h-5 w-5 text-blue-500 inline" />,
    label: 'Axes',
    form: <AxesForm />,
    defaultOpen: false,
  },
  Animation: {
    icon: <ClockIcon className="h-5 w-5 text-blue-500 inline" />,
    label: 'Animation',
    form: <AnimationForm />,
    defaultOpen: false,
  },
  Grid: {
    icon: <GridIcon className="h-5 w-5 text-blue-500 inline" />,
    label: 'Grille',
    form: <GridForm />,
    defaultOpen: false,
  },
  Tooltip: {
    icon: <InfoCircledIcon className="h-5 w-5 text-blue-500 inline" />,
    label: 'Info-bulle',
    form: 'Info-bulle',
    defaultOpen: false,
  },
  Pie: {
    icon: <PieChartIcon className="h-5 w-5 text-blue-500 inline" />,
    label: 'Camembert',
    form: <PieForm />,
    defaultOpen: false,
  },
  Line: {
    icon: <LineChartIcon className="h-5 w-5 text-blue-500 inline" />,
    label: 'Courbe',
    form: <LineForm />,
    defaultOpen: false,
  },
  Bubble: {
    icon: <BubbleChartIcon className="h-5 w-5 text-blue-500 inline" />,
    label: 'Bulles',
    form: <BubbleForm />,
    defaultOpen: false,
  },
  Area: {
    icon: <AreaChartIcon className="h-5 w-5 text-blue-500 inline" />,
    label: 'Aires',
    form: <AreaForm />,
    defaultOpen: false,
  },
};

const CHART_CONFIG_PANEL = {
  pie: ['Indicators', 'Appearance', 'Pie', 'Animation'],
  column: ['Indicators', 'Appearance', 'Axis', 'Animation', 'Grid'],
  bar: ['Indicators', 'Appearance', 'Axis', 'Animation', 'Grid'],
  line: ['Indicators', 'Appearance', 'Line', 'Axis', 'Animation', 'Grid'],
  scatter: ['Indicators', 'Appearance', 'Axis', 'Animation', 'Grid'],
  bubble: ['Indicators', 'Appearance', 'Axis', 'Bubble', 'Animation', 'Grid'],
  area: ['Indicators', 'Appearance', 'Axis', 'Area', 'Animation', 'Grid'],
  lineColumn: ['Indicators', 'Appearance', 'Axis', 'Line', 'Animation', 'Grid'],
};

export const ConfigPanel = () => {
  const { chart: chartConfig } = useChartConfig();
  const accordionItems = CHART_CONFIG_PANEL[chartConfig.type];
  const defaultOpenItem =
    accordionItems.find(
      (item) =>
        item in CHART_CONFIG_FORMS &&
        CHART_CONFIG_FORMS[item as keyof typeof CHART_CONFIG_FORMS].defaultOpen
    ) ?? accordionItems[0];

  return (
    <Well title={'Paramètres'} Icon={MixerHorizontalIcon}>
      <Accordion
        type="single"
        defaultValue={defaultOpenItem}
        collapsible={false}
      >
        {accordionItems.map((item) => {
          const formConfig =
            CHART_CONFIG_FORMS[item as keyof typeof CHART_CONFIG_FORMS];
          const label = formConfig?.label ?? item;

          return (
            <AccordionItem key={item} value={item}>
              <AccordionTrigger>
                <div className="text-md font-medium text-gray-900 title-font">
                  {formConfig?.icon ?? 'Icône manquante'} {label}
                </div>
              </AccordionTrigger>
              <AccordionContent>
                {formConfig?.form ?? 'Formulaire manquant'}
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </Well>
  );
};
